import { useQuery } from "@tanstack/react-query";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type { LtlOwner } from "~/shared/schemas/customer-schema.ts";
import { ltlOwnerSchema } from "~/shared/schemas/customer-schema.ts";
import type {
	CustomerQuoteBoardItem,
	QuoteBoardItem,
} from "~/shared/schemas/quote-board-schema.ts";
import {
	customerQuoteBoardItemSchema,
	QuoteBoardItemSchema,
} from "~/shared/schemas/quote-board-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import type { GetQuoteBoardType } from "~/shared/types/quote-board-types";

import { timeFrames } from "../constants/time-frame-constants.ts";

// Append internal-only array filters to the URL params
const appendInternalArrayFilters = (
	queryParams: URLSearchParams,
	ltlBrokers: number[],
	customers: number[],
	laes: number[],
): void => {
	const arrayParams: Record<string, number[]> = {
		ltlBrokerIds: ltlBrokers,
		customerIds: customers,
		primaryBrokerIds: laes,
	};

	for (const [key, values] of Object.entries(arrayParams)) {
		if (values.length > 0) {
			for (const id of values) {
				queryParams.append(key, id.toString());
			}
		}
	}
};

// Helper function to build query parameters
const buildQueryParams = (
	params: GetQuoteBoardType,
	isExternal = false,
): URLSearchParams => {
	const {
		timeFrame,
		ltlBrokers = [],
		customers = [],
		laes = [],
		quoteId,
	} = params;
	const queryParams = new URLSearchParams();

	// When searching by Quote ID, skip all other filters
	if (quoteId) {
		queryParams.append("quoteId", quoteId.toString());
		queryParams.append("hideExpiredQuotes", "false");
		return queryParams;
	}

	// Internal-only filters: skip for external endpoint.
	if (!isExternal) {
		appendInternalArrayFilters(queryParams, ltlBrokers, customers, laes);
	}

	// Append conditional parameters
	if (timeFrame) {
		if (isExternal) {
			const days = timeFrames.find((tf) => tf.value === timeFrame)?.days;
			if (days) {
				queryParams.append("daysInPast", days.toString());
			}
		} else {
			queryParams.append("timeframe", timeFrame.toString());
		}
	}
	queryParams.append("hideExpiredQuotes", "false");

	return queryParams;
};

// Helper function to fetch internal quote board items
const fetchInternalQuoteBoardItems = async (
	params: GetQuoteBoardType,
): Promise<QuoteBoardItem[]> => {
	const queryParams = buildQueryParams(params);
	const result = await getAndValidate(
		`${apiRoutes.QUOTEBOARDV2}?${queryParams.toString()}`,
		QuoteBoardItemSchema.array(),
	);
	if (result.success) {
		return result.data as QuoteBoardItem[];
	}
	console.error("Failed to fetch quote board items:", result.error);
	return [];
};

// Helper function to fetch external quote board items
const fetchExternalQuoteBoardItems = async (
	params?: GetQuoteBoardType,
): Promise<CustomerQuoteBoardItem[]> => {
	const queryParams = buildQueryParams(params ?? {}, true);
	const result = await getAndValidate(
		`${apiRoutes.CUSTOMERQUOTEBOARD}?${queryParams.toString()}`,
		customerQuoteBoardItemSchema.array(),
	);
	if (result.success) {
		return result.data.map((quote) => ({
			...quote,
			customerRate: quote.carrierRate,
		})) as CustomerQuoteBoardItem[];
	}
	console.error("Failed to fetch quote board items:", result.error);
	return [];
};

// Helper function to check if there are search criteria
const hasSearchCriteria = (params: GetQuoteBoardType): boolean => {
	const { quoteId, ltlBrokers = [], customers = [], laes = [] } = params;
	return [
		quoteId && quoteId > 0,
		ltlBrokers.length > 0,
		customers.length > 0,
		laes.length > 0,
	].some(Boolean);
};

export const useGetQuoteBoardItems = (params?: GetQuoteBoardType) => {
	const effectiveParams = params ?? {};
	const { isInternal } = useDeploymentType();

	return useQuery<QuoteBoardItem[] | CustomerQuoteBoardItem[]>({
		queryKey: ["quote-board-items", ...(params ? Object.values(params) : [])],
		queryFn: async () => {
			if (!isInternal) {
				return fetchExternalQuoteBoardItems(effectiveParams);
			}
			if (!hasSearchCriteria(effectiveParams)) {
				return [];
			}

			return fetchInternalQuoteBoardItems(effectiveParams);
		},
		enabled: params?.shouldRun !== false,
		staleTime: 0,
		gcTime: 0,
	});

	// return {
	// 	...query,
	// 	refetch: query.refetch
	// };
};

export const useSearchArsByName = (userInput: string) => {
	return useQuery<LtlOwner[]>({
		queryKey: ["quote-board-ars", "user-input", userInput],
		queryFn: async (): Promise<LtlOwner[]> => {
			// internal quote data
			const result = await getAndValidate(
				`${apiRoutes.GETLTLBROKERS.replace(
					":userInput",
					encodeURIComponent(userInput),
				)}`,
				ltlOwnerSchema.array(),
			);
			if (result.success) {
				return result.data as LtlOwner[];
			}
			// if request fails return an empty array for component to handle failure
			console.error("Failed to fetch quote board items:", result.error);
			return [];
		},
	});
};

const getLtlBrokersRoute = (searchTerm: string) => {
	return apiRoutes.GETLTLBROKERS.replace(
		":userInput",
		encodeURIComponent(searchTerm),
	);
};

export const useQuoteBoardManagement = () => {
	return {
		getLtlBrokersRoute,
		useSearchArsByName,
	};
};
